import {
  CREATIVE_TEMPLATE_DEFAULT_ARTBOARD_ID,
  CREATIVE_TEMPLATE_DEFAULT_ARTBOARD_SIZE,
  CREATIVE_TEMPLATE_CONTINUOUS_SLIDE_MIN,
  CREATIVE_TEMPLATE_LAYOUT_KIND,
  CREATIVE_TEMPLATE_SLIDE_MAX,
  CREATIVE_TEMPLATE_SLIDE_MIN,
} from "../../../utils/creativeTemplateLayout.js";
import {
  buildStripArtboard,
  getContinuousSlideCount,
} from "./continuousStrip.js";
import { getArtboardVariableMismatches } from "./nodeVariables.js";

const PRESET_ORDER = ["square", "portrait", "story", "landscape"];

const cloneTree = (value) => structuredClone(value ?? {});

const countNodes = ({ nodes = {} }) =>
  Object.values(nodes || {}).reduce((total, node) => {
    return total + 1 + countNodes({ nodes: node.children || {} });
  }, 0);

const createEmptyArtboard = ({ width, height, label }) => ({
  label,
  width: Math.round(Number(width) || 0),
  height: Math.round(Number(height) || 0),
  nodes: {},
  nodeLayouts: {},
});

const patchActiveArtboardGraph = ({
  artboards = {},
  artboardID,
  nodes,
  nodeLayouts,
}) => {
  const current = artboards[artboardID] || {};
  return {
    ...artboards,
    [artboardID]: {
      ...current,
      ...(nodes !== undefined ? { nodes } : {}),
      ...(nodeLayouts !== undefined ? { nodeLayouts } : {}),
    },
  };
};

const listArtboardIDs = ({ artboards = {} }) => {
  const ids = Object.keys(artboards || {});
  const rank = (id) => {
    if (id === CREATIVE_TEMPLATE_DEFAULT_ARTBOARD_ID) return 0;
    const presetIndex = PRESET_ORDER.indexOf(id);
    if (presetIndex >= 0) return 1 + presetIndex;
    return 10;
  };
  return ids.sort((left, right) => {
    const rankDiff = rank(left) - rank(right);
    if (rankDiff !== 0) return rankDiff;
    return left.localeCompare(right);
  });
};

export const buildCellKey = ({ artboardID, slideID } = {}) => {
  const formatID = String(artboardID || "").trim();
  const slide = String(slideID || "").trim();
  if (!slide) return formatID;
  return `${formatID}__${slide}`;
};

export const parseCellKey = ({ cellKey } = {}) => {
  const raw = String(cellKey || "").trim();
  const separator = "__";
  const index = raw.indexOf(separator);
  if (index <= 0) {
    return { artboardID: raw, slideID: "" };
  }
  return {
    artboardID: raw.slice(0, index),
    slideID: raw.slice(index + separator.length),
  };
};

const listRawStoredIDs = ({ value } = {}) => {
  if (Array.isArray(value)) {
    return value.map((id) => String(id || "").trim()).filter(Boolean);
  }
  if (value && typeof value === "object") {
    return Object.keys(value)
      .sort((left, right) => Number(left) - Number(right))
      .map((key) => String(value[key] || "").trim())
      .filter(Boolean);
  }
  return [];
};

export const normalizeFormatIDs = ({ value } = {}) => {
  const seen = new Set();
  const ids = [];
  listRawStoredIDs({ value }).forEach((id) => {
    const formatID = parseCellKey({ cellKey: id }).artboardID || id;
    if (!formatID || seen.has(formatID)) return;
    seen.add(formatID);
    ids.push(formatID);
  });
  return ids;
};

export const resolveLayoutKind = ({ layoutKind, slides } = {}) => {
  if (
    layoutKind === CREATIVE_TEMPLATE_LAYOUT_KIND.CAROUSEL &&
    Array.isArray(slides) &&
    slides.length >= CREATIVE_TEMPLATE_SLIDE_MIN
  ) {
    return CREATIVE_TEMPLATE_LAYOUT_KIND.CAROUSEL;
  }
  if (layoutKind === CREATIVE_TEMPLATE_LAYOUT_KIND.CONTINUOUS_CAROUSEL) {
    return CREATIVE_TEMPLATE_LAYOUT_KIND.CONTINUOUS_CAROUSEL;
  }
  return CREATIVE_TEMPLATE_LAYOUT_KIND.SINGLE;
};

export const isCarouselLayout = ({ layoutKind, slides } = {}) =>
  resolveLayoutKind({ layoutKind, slides }) ===
  CREATIVE_TEMPLATE_LAYOUT_KIND.CAROUSEL;

export const isContinuousLayout = ({ layoutKind, slides } = {}) =>
  resolveLayoutKind({ layoutKind, slides }) ===
  CREATIVE_TEMPLATE_LAYOUT_KIND.CONTINUOUS_CAROUSEL;

// Output slides produced per format: N for carousel/continuous, 1 for single.
export const getProduceSlideCount = ({
  layoutKind,
  slides = [],
  layoutConfigurations,
} = {}) => {
  if (isContinuousLayout({ layoutKind, slides })) {
    return getContinuousSlideCount({ layoutConfigurations });
  }
  if (isCarouselLayout({ layoutKind, slides })) {
    return Math.max(1, (slides || []).length || 1);
  }
  return 1;
};

export const clampSlideCount = ({ count } = {}) => {
  const parsed = Number(count);
  if (!Number.isInteger(parsed)) return CREATIVE_TEMPLATE_SLIDE_MIN;
  if (parsed < CREATIVE_TEMPLATE_SLIDE_MIN) return CREATIVE_TEMPLATE_SLIDE_MIN;
  if (parsed > CREATIVE_TEMPLATE_SLIDE_MAX) return CREATIVE_TEMPLATE_SLIDE_MAX;
  return parsed;
};

export const canAddSlide = ({ slides = [] } = {}) =>
  (slides?.length || 0) < CREATIVE_TEMPLATE_SLIDE_MAX;

export const canRemoveSlide = ({ slides = [] } = {}) =>
  (slides?.length || 0) > CREATIVE_TEMPLATE_SLIDE_MIN;

export const nextSlideID = ({ slides = [] } = {}) => {
  const used = new Set(
    (slides || []).map((slide) => slide?.id).filter(Boolean)
  );
  let index = 1;
  while (used.has(`slide_${index}`)) index += 1;
  return `slide_${index}`;
};

export const createSeedArtboards = ({
  width = CREATIVE_TEMPLATE_DEFAULT_ARTBOARD_SIZE,
  height = CREATIVE_TEMPLATE_DEFAULT_ARTBOARD_SIZE,
  label = "source",
} = {}) => ({
  [CREATIVE_TEMPLATE_DEFAULT_ARTBOARD_ID]: createEmptyArtboard({
    width,
    height,
    label,
  }),
});

export const seedCarouselSlides = ({ count, artboards } = {}) => {
  const slideCount = clampSlideCount({ count });
  const seed = cloneTree(artboards || createSeedArtboards());
  return Array.from({ length: slideCount }, (_, index) => ({
    id: `slide_${index + 1}`,
    artboards: cloneTree(seed),
  }));
};

export const pickSingleFormatArtboards = ({
  artboards = {},
  artboardID,
} = {}) => {
  const preferredID =
    (artboardID && artboards[artboardID] && artboardID) ||
    (artboards[CREATIVE_TEMPLATE_DEFAULT_ARTBOARD_ID] &&
      CREATIVE_TEMPLATE_DEFAULT_ARTBOARD_ID) ||
    listArtboardIDs({ artboards })[0];
  if (!preferredID || !artboards[preferredID]) {
    return {
      artboards: createSeedArtboards(),
      artboardID: CREATIVE_TEMPLATE_DEFAULT_ARTBOARD_ID,
    };
  }
  return {
    artboards: { [preferredID]: cloneTree(artboards[preferredID]) },
    artboardID: preferredID,
  };
};

export const convertToLayoutKind = ({
  nextLayoutKind,
  artboards = {},
  activeArtboardID,
  slideCount = CREATIVE_TEMPLATE_CONTINUOUS_SLIDE_MIN,
  stripFormat = null,
} = {}) => {
  const single = pickSingleFormatArtboards({
    artboards,
    artboardID: activeArtboardID,
  });
  switch (nextLayoutKind) {
    case CREATIVE_TEMPLATE_LAYOUT_KIND.CAROUSEL: {
      const slides = seedCarouselSlides({
        count: CREATIVE_TEMPLATE_SLIDE_MIN,
        artboards: single.artboards,
      });
      return {
        layoutKind: CREATIVE_TEMPLATE_LAYOUT_KIND.CAROUSEL,
        slides,
        artboards: slides[0]?.artboards || single.artboards,
        activeSlideID: slides[0]?.id || "",
        activeArtboardID: single.artboardID,
      };
    }
    case CREATIVE_TEMPLATE_LAYOUT_KIND.CONTINUOUS_CAROUSEL: {
      const count = getContinuousSlideCount({
        layoutConfigurations: { continuousCarousel: { slideCount } },
      });
      const source = stripFormat || single.artboards[single.artboardID];
      const stripArtboardID = stripFormat?.id || single.artboardID;
      return {
        layoutKind: CREATIVE_TEMPLATE_LAYOUT_KIND.CONTINUOUS_CAROUSEL,
        slides: [],
        artboards: {
          [stripArtboardID]: buildStripArtboard({
            slideWidth: source?.width,
            slideHeight: source?.height,
            slideCount: count,
            label: source?.label,
          }),
        },
        layoutConfigurations: { continuousCarousel: { slideCount: count } },
        activeSlideID: "",
        activeArtboardID: stripArtboardID,
      };
    }
    case CREATIVE_TEMPLATE_LAYOUT_KIND.SINGLE:
      return {
        layoutKind: CREATIVE_TEMPLATE_LAYOUT_KIND.SINGLE,
        slides: [],
        artboards: single.artboards,
        activeSlideID: "",
        activeArtboardID: single.artboardID,
      };
    default: {
      const exhaustive = nextLayoutKind;
      throw new Error(`Unhandled layoutKind: ${exhaustive}`);
    }
  }
};

export const collectFormatCatalog = ({ slides = [], artboards = {} } = {}) => {
  const catalog = {};
  const remember = (id, artboard) => {
    if (!id || !artboard || catalog[id]) return;
    catalog[id] = {
      width: artboard.width,
      height: artboard.height,
      label: artboard.label,
    };
  };
  Object.entries(artboards || {}).forEach(([id, artboard]) =>
    remember(id, artboard)
  );
  (slides || []).forEach((slide) => {
    Object.entries(slide?.artboards || {}).forEach(([id, artboard]) =>
      remember(id, artboard)
    );
  });
  return catalog;
};

export const alignSlideFormats = ({ slides = [], artboards = {} } = {}) => {
  const catalog = collectFormatCatalog({ slides, artboards });
  const formatIDs = listArtboardIDs({ artboards: catalog });
  if (formatIDs.length === 0) return slides || [];
  return (slides || []).map((slide) => {
    const current = slide?.artboards || {};
    const nextArtboards = { ...current };
    let changed = false;
    formatIDs.forEach((id) => {
      if (nextArtboards[id]) return;
      nextArtboards[id] = createEmptyArtboard(catalog[id]);
      changed = true;
    });
    return changed ? { ...slide, artboards: nextArtboards } : slide;
  });
};

const artboardsWithCatalog = ({ artboards = {}, catalog = {} } = {}) => {
  const next = { ...artboards };
  Object.entries(catalog || {}).forEach(([id, meta]) => {
    if (next[id]) return;
    next[id] = createEmptyArtboard(meta);
  });
  return next;
};

export const getSlideByID = ({ slides = [], slideID } = {}) =>
  (slides || []).find((slide) => slide?.id === slideID) || null;

export const resolveActiveSlideID = ({ slides = [], activeSlideID } = {}) => {
  if (activeSlideID && getSlideByID({ slides, slideID: activeSlideID })) {
    return activeSlideID;
  }
  return slides?.[0]?.id || "";
};

export const getWorkingArtboards = ({
  layoutKind,
  slides = [],
  artboards = {},
  activeSlideID,
} = {}) => {
  if (!isCarouselLayout({ layoutKind, slides })) return artboards || {};
  const slide = getSlideByID({
    slides,
    slideID: resolveActiveSlideID({ slides, activeSlideID }),
  });
  return slide?.artboards || {};
};

export const getFormatArtboards = ({
  doc,
  layoutKind,
  slides,
  artboards,
} = {}) => {
  const resolvedSlides = slides || doc?.slides || [];
  const resolvedKind = layoutKind || doc?.layoutKind;
  if (isCarouselLayout({ layoutKind: resolvedKind, slides: resolvedSlides })) {
    const aligned = alignSlideFormats({
      slides: resolvedSlides,
      artboards: artboards || resolvedSlides[0]?.artboards || {},
    });
    return aligned[0]?.artboards || {};
  }
  return artboards || doc?.artboards || {};
};

export const getSlideWorkingArtboards = ({ doc, slideID } = {}) => {
  if (!isCarouselLayout(doc)) return doc?.artboards || {};
  const slide =
    getSlideByID({
      slides: doc?.slides,
      slideID: slideID || doc?.editor?.activeSlideID,
    }) || doc?.slides?.[0];
  return slide?.artboards || {};
};

export const hydrateLayoutFromDoc = ({ doc } = {}) => {
  const layoutKind = resolveLayoutKind({
    layoutKind: doc?.layoutKind,
    slides: doc?.slides,
  });
  switch (layoutKind) {
    case CREATIVE_TEMPLATE_LAYOUT_KIND.CAROUSEL: {
      const slides = alignSlideFormats({
        slides: Array.isArray(doc?.slides) ? doc.slides : [],
      });
      const activeSlideID = resolveActiveSlideID({
        slides,
        activeSlideID: doc?.editor?.activeSlideID,
      });
      return {
        layoutKind,
        slides,
        activeSlideID,
        artboards: getWorkingArtboards({
          layoutKind,
          slides,
          activeSlideID,
        }),
      };
    }
    case CREATIVE_TEMPLATE_LAYOUT_KIND.CONTINUOUS_CAROUSEL:
      return {
        layoutKind,
        slides: [],
        activeSlideID: "",
        artboards: doc?.artboards || {},
        layoutConfigurations: {
          continuousCarousel: {
            slideCount: getContinuousSlideCount({
              layoutConfigurations: doc?.layoutConfigurations,
            }),
          },
        },
      };
    case CREATIVE_TEMPLATE_LAYOUT_KIND.SINGLE:
      return {
        layoutKind,
        slides: [],
        activeSlideID: "",
        artboards: doc?.artboards || {},
      };
    default: {
      const exhaustive = layoutKind;
      throw new Error(`Unhandled layoutKind: ${exhaustive}`);
    }
  }
};

export const patchSlideArtboards = ({ slides = [], slideID, artboards } = {}) =>
  (slides || []).map((slide) =>
    slide?.id === slideID ? { ...slide, artboards } : slide
  );

export const flushActiveCell = ({
  layoutKind,
  slides = [],
  artboards = {},
  activeSlideID,
  activeArtboardID,
  nodes,
  nodeLayouts,
} = {}) => {
  const flushedArtboards = patchActiveArtboardGraph({
    artboards,
    artboardID: activeArtboardID,
    nodes,
    nodeLayouts,
  });
  if (!isCarouselLayout({ layoutKind, slides })) {
    return { artboards: flushedArtboards, slides };
  }
  const slideID = resolveActiveSlideID({ slides, activeSlideID });
  return {
    artboards: flushedArtboards,
    slides: patchSlideArtboards({
      slides,
      slideID,
      artboards: flushedArtboards,
    }),
  };
};

export const fanOutFormat = ({ slides = [], artboardID, artboard } = {}) => {
  if (!artboardID || !artboard) return slides || [];
  return alignSlideFormats({ slides }).map((slide) => ({
    ...slide,
    artboards: {
      ...(slide?.artboards || {}),
      [artboardID]: cloneTree(artboard),
    },
  }));
};

export const removeFormatFromSlides = ({ slides = [], artboardID } = {}) => {
  if (!artboardID) return slides || [];
  return (slides || []).map((slide) => {
    const nextArtboards = { ...(slide?.artboards || {}) };
    delete nextArtboards[artboardID];
    return { ...slide, artboards: nextArtboards };
  });
};

export const mapSlideArtboards = ({ slides = [], mapper } = {}) =>
  (slides || []).map((slide) => ({
    ...slide,
    artboards:
      typeof mapper === "function"
        ? mapper(slide?.artboards || {})
        : slide?.artboards || {},
  }));

export const duplicateSlide = ({ slides = [], slideID } = {}) => {
  if (!canAddSlide({ slides })) return slides || [];
  const aligned = alignSlideFormats({ slides });
  const index = aligned.findIndex((slide) => slide?.id === slideID);
  if (index < 0) return slides || [];
  const source = aligned[index];
  const copy = {
    id: nextSlideID({ slides: aligned }),
    artboards: cloneTree(source?.artboards || {}),
  };
  return [...aligned.slice(0, index + 1), copy, ...aligned.slice(index + 1)];
};

export const addSlide = ({ slides = [], artboards, empty = false } = {}) => {
  if (!canAddSlide({ slides })) return slides || [];
  const aligned = alignSlideFormats({ slides, artboards });
  const catalog = collectFormatCatalog({ slides: aligned, artboards });
  const source = artboardsWithCatalog({
    artboards:
      artboards ||
      aligned[aligned.length - 1]?.artboards ||
      createSeedArtboards(),
    catalog,
  });
  const nextArtboards = empty
    ? Object.fromEntries(
        listArtboardIDs({ artboards: source }).map((id) => [
          id,
          createEmptyArtboard({
            width: source[id]?.width,
            height: source[id]?.height,
            label: source[id]?.label,
          }),
        ])
      )
    : cloneTree(source);
  return [
    ...aligned,
    {
      id: nextSlideID({ slides: aligned }),
      artboards: nextArtboards,
    },
  ];
};

export const removeSlide = ({ slides = [], slideID } = {}) => {
  if (!canRemoveSlide({ slides })) return slides || [];
  const next = (slides || []).filter((slide) => slide?.id !== slideID);
  return next.length >= CREATIVE_TEMPLATE_SLIDE_MIN
    ? alignSlideFormats({ slides: next })
    : slides;
};

export const reorderSlides = ({ slides = [], activeID, overID } = {}) => {
  const current = slides || [];
  const from = current.findIndex((slide) => slide?.id === activeID);
  const to = current.findIndex((slide) => slide?.id === overID);
  if (from < 0 || to < 0 || from === to) return current;
  const next = [...current];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
};

export const listSelectedSlides = ({ slides = [], slideIDs } = {}) => {
  const requested = Array.isArray(slideIDs)
    ? slideIDs.map((id) => String(id || "").trim()).filter(Boolean)
    : [];
  if (requested.length === 0) return slides || [];
  const requestedSet = new Set(requested);
  return (slides || []).filter((slide) => requestedSet.has(slide?.id));
};

export const listAllCells = ({
  layoutKind,
  slides = [],
  artboards = {},
  artboardIDs,
  slideIDs,
} = {}) => {
  if (!isCarouselLayout({ layoutKind, slides })) {
    const ids =
      Array.isArray(artboardIDs) && artboardIDs.length
        ? artboardIDs
        : listArtboardIDs({ artboards });
    return ids
      .filter((artboardID) => artboards?.[artboardID])
      .map((artboardID) => ({
        artboardID,
        slideID: "",
        slideIndex: 0,
        cellKey: artboardID,
        artboard: artboards[artboardID],
      }));
  }
  const selectedSlides = alignSlideFormats({
    slides: listSelectedSlides({ slides, slideIDs }),
  });
  const formatIDs =
    Array.isArray(artboardIDs) && artboardIDs.length
      ? artboardIDs
      : listArtboardIDs({
          artboards: collectFormatCatalog({ slides: selectedSlides }),
        });
  const cells = [];
  selectedSlides.forEach((slide, slideIndex) => {
    formatIDs.forEach((artboardID) => {
      cells.push({
        artboardID,
        slideID: slide?.id || "",
        slideIndex,
        cellKey: buildCellKey({ artboardID, slideID: slide?.id }),
        artboard: slide?.artboards?.[artboardID] || null,
      });
    });
  });
  return cells;
};

export const findMissingOrEmptyCell = ({ cells = [] } = {}) =>
  cells.find((cell) => {
    if (!cell?.artboard) return true;
    return countNodes({ nodes: cell.artboard?.nodes || {} }) < 1;
  }) || null;

export const flattenCellsToArtboards = ({ cells = [] } = {}) => {
  const next = {};
  cells.forEach((cell) => {
    if (!cell?.cellKey || !cell.artboard) return;
    next[cell.cellKey] = cell.artboard;
  });
  return next;
};

export const getLayoutVariableMismatches = ({
  layoutKind,
  slides = [],
  artboards = {},
} = {}) => {
  if (!isCarouselLayout({ layoutKind, slides })) {
    return getArtboardVariableMismatches({ artboards }).map((item) => ({
      ...item,
      slideID: "",
      slideIndex: -1,
    }));
  }
  return (slides || []).flatMap((slide, slideIndex) =>
    getArtboardVariableMismatches({
      artboards: slide?.artboards || {},
    }).map((item) => ({
      ...item,
      slideID: slide?.id || "",
      slideIndex,
    }))
  );
};

export const countAllLayoutNodes = ({
  layoutKind,
  slides = [],
  artboards = {},
  nodes,
} = {}) => {
  const cells = listAllCells({ layoutKind, slides, artboards });
  const fromCells = cells.reduce(
    (total, cell) => total + countNodes({ nodes: cell?.artboard?.nodes || {} }),
    0
  );
  if (fromCells > 0) return fromCells;
  if (isCarouselLayout({ layoutKind, slides })) return 0;
  return countNodes({ nodes: nodes || {} });
};

export const getActiveSlideMirrorArtboards = ({
  layoutKind,
  slides = [],
  artboards = {},
  activeSlideID,
} = {}) =>
  getWorkingArtboards({
    layoutKind,
    slides,
    artboards,
    activeSlideID,
  });

export const getSlideAnalysis = ({ analysisBySlide = {}, slideID } = {}) =>
  (slideID && analysisBySlide?.[slideID]) || null;

export const isProcessingAnalysis = ({ analysis } = {}) =>
  analysis?.status === "processing";

export const listProcessingSlideIDs = ({ analysisBySlide = {} } = {}) =>
  Object.entries(analysisBySlide || {})
    .filter(([, analysis]) => isProcessingAnalysis({ analysis }))
    .map(([slideID]) => slideID);

export const findSlideIndex = ({ slides = [], slideID } = {}) =>
  (slides || []).findIndex((slide) => slide?.id === slideID);

export const mergeAnalyzingSlides = ({
  slides = [],
  existingSlides = [],
  processingSlideIDs = [],
} = {}) => {
  const locked = new Set(
    (processingSlideIDs || [])
      .map((id) => String(id || "").trim())
      .filter(Boolean)
  );
  if (locked.size === 0) return slides || [];
  const existingByID = Object.fromEntries(
    (existingSlides || [])
      .filter((slide) => slide?.id)
      .map((slide) => [slide.id, slide])
  );
  return (slides || []).map((slide) =>
    locked.has(slide?.id) && existingByID[slide.id]
      ? existingByID[slide.id]
      : slide
  );
};
